import { CHEMICAL_FIELDS } from "../chemicalLibrary.constants";
import {
  ADD_EDIT_FORM_CONTROLS,
  SELECTED_CHEMICAL_FIELD_CONFIG,
} from "./ChemicalLibraryAddEditDialog.constants";

const ADD_CHEMICAL_CATEGORY_OPTIONS = [
  { value: "acid", label: "acid" },
  { value: "alkali", label: "alkali" },
  { value: "additive", label: "additive" },
];

const CHEMICAL_CONCENTRATION_RANGE = {
  "Hydrochloric Acid": { min: 0, max: 50 },
  "Sodium Chloride": { min: 0, max: 50 },
  "Sodium Hydroxide": { min: 0, max: 50 },
  "Sodium Carbonate": { min: 0, max: 50 },
  "Sodium Hypochlorite": { min: 0, max: 50 },
};

export const getChemicalConcentrationRange = (chemicalName) => {
  return CHEMICAL_CONCENTRATION_RANGE[chemicalName] || { min: 0, max: 100 };
};

const TEMPERATURE = 25;

const normalizeSymbol = (symbol = "") => {
  return `${symbol}`
    .replace(/₀/g, "0")
    .replace(/₁/g, "1")
    .replace(/₂/g, "2")
    .replace(/₃/g, "3")
    .replace(/₄/g, "4")
    .replace(/₅/g, "5")
    .replace(/₆/g, "6")
    .replace(/₇/g, "7")
    .replace(/₈/g, "8")
    .replace(/₉/g, "9")
    .replace(/\s+/g, "")
    .toUpperCase();
};

const getProperty = (obj, keys = []) => {
  for (const key of keys) {
    const value = obj?.[key];
    if (value !== undefined && value !== null && value !== "") {
      return Number(value);
    }
  }
  return null;
};

const toNumeric = (value) => {
  if (typeof value === "number") {
    return Number.isFinite(value) ? value : NaN;
  }

  if (typeof value === "string") {
    const normalized = value.replace(/[^0-9.-]/g, "");
    return Number.parseFloat(normalized);
  }

  return Number(value);
};

const waterDensityAt25 = () => {
  const t = TEMPERATURE;
  return (
    (999.83952 +
      16.945176 * t +
      -0.0079870401 * t ** 2 +
      -0.000046170461 * t ** 3 +
      0.00000010556302 * t ** 4 +
      -2.8054253e-10 * t ** 5) /
    (1 + 0.01687985 * t)
  );
};

const calculateSpecificGravity = (bulkConcPer, molecularWeight, coeffs) => {
  if (!molecularWeight) {
    return null;
  }

  const avPitzer = Math.exp(
    0.411276653 + 0.008251869 * TEMPERATURE + 0.0000162678 * TEMPERATURE ** 2,
  );
  const concMolal =
    ((bulkConcPer / 100 / (1 - bulkConcPer / 100)) * 1000) / molecularWeight;
  const volume =
    coeffs.CSA +
    (coeffs.CSB / 1.2) *
      avPitzer *
      Math.log(1 + 1.2 * Math.sqrt(coeffs.CSB * concMolal)) +
    coeffs.CSC * concMolal +
    coeffs.CSD * concMolal ** 2 +
    coeffs.CSE * concMolal ** 3;

  return (
    (1000 + concMolal * molecularWeight) /
    (1000 + ((concMolal * waterDensityAt25()) / 1000) * volume)
  );
};

const calculatePolynomialGravity = (bulkConcPer, coeffs) => {
  return (
    coeffs.CSBP +
    coeffs.CSCP * bulkConcPer +
    coeffs.CSDP * bulkConcPer ** 2 +
    coeffs.CSEP * bulkConcPer ** 3
  );
};

export const calculateDensityFromConcentration = (
  selectedChemical,
  bulkConcentration,
) => {
  const concentration = toNumeric(bulkConcentration);
  if (!selectedChemical || Number.isNaN(concentration)) {
    return selectedChemical?.bulk_density ?? "0.00";
  }

  const molecularWeight = getProperty(selectedChemical, [
    "molecularWeight",
    "molecular_weight",
    "molecular_weight_value",
  ]);

  const coeffs = {
    CSA:
      getProperty(selectedChemical, [
        "coeffSpecGravityA",
        "coeff_spec_gravity_a",
      ]) || 0,
    CSB:
      getProperty(selectedChemical, [
        "coeffSpecGravityB",
        "coeff_spec_gravity_b",
      ]) || 0,
    CSC:
      getProperty(selectedChemical, [
        "coeffSpecGravityC",
        "coeff_spec_gravity_c",
      ]) || 0,
    CSD:
      getProperty(selectedChemical, [
        "coeffSpecGravityD",
        "coeff_spec_gravity_d",
      ]) || 0,
    CSE:
      getProperty(selectedChemical, [
        "coeffSpecGravityE",
        "coeff_spec_gravity_e",
      ]) || 0,
    CSBP:
      getProperty(selectedChemical, [
        "coeffSpecGravityBPrime",
        "coeff_spec_gravity_b_prime",
      ]) || 0,
    CSCP:
      getProperty(selectedChemical, [
        "coeffSpecGravityCPrime",
        "coeff_spec_gravity_c_prime",
      ]) || 0,
    CSDP:
      getProperty(selectedChemical, [
        "coeffSpecGravityDPrime",
        "coeff_spec_gravity_d_prime",
      ]) || 0,
    CSEP:
      getProperty(selectedChemical, [
        "coeffSpecGravityEPrime",
        "coeff_spec_gravity_e_prime",
      ]) || 0,
  };

  const hasCoefficientData =
    molecularWeight &&
    [coeffs.CSA, coeffs.CSB, coeffs.CSC, coeffs.CSD, coeffs.CSE].some(
      (value) => value !== 0,
    );

  const fallbackDensityFromBase = () => {
    const baseDensity = toNumeric(selectedChemical?.bulk_density);
    const baseConcentration = toNumeric(selectedChemical?.bulk_concentration);

    if (
      Number.isNaN(baseDensity) ||
      Number.isNaN(baseConcentration) ||
      baseConcentration <= 0
    ) {
      return selectedChemical?.bulk_density ?? "0.00";
    }

    // Keep the selected chemical's current point and linearly project from 0% ~= 1.0.
    const projected =
      1 + ((baseDensity - 1) * concentration) / baseConcentration;
    return Number.isFinite(projected)
      ? projected
      : selectedChemical?.bulk_density;
  };

  if (!hasCoefficientData) {
    return fallbackDensityFromBase();
  }

  const normalizedSymbol = normalizeSymbol(selectedChemical?.symbol);
  const isSulfuricAcid = normalizedSymbol === "H2SO4";

  const density = isSulfuricAcid
    ? calculatePolynomialGravity(concentration, coeffs)
    : calculateSpecificGravity(concentration, molecularWeight, coeffs);

  if (density === null || Number.isNaN(density)) {
    return fallbackDensityFromBase();
  }

  return density;
};

export const formatChemicalDisplayName = (symbol, concentration) => {
  const trimmedSymbol = `${symbol ?? ""}`.trim();
  if (!trimmedSymbol) {
    return "";
  }

  const concentrationValue = `${concentration ?? ""}`.trim();
  if (!concentrationValue) {
    return trimmedSymbol;
  }

  return `${trimmedSymbol}(${concentrationValue})`;
};

export const buildChemicalOptionsByCategory = (chemicalLibrary = []) => {
  const grouped = {
    acid: [],
    alkali: [],
    cip_additive: [],
  };

  (Array.isArray(chemicalLibrary) ? chemicalLibrary : []).forEach((item) => {
    const category = item?.category;
    if (!grouped[category]) {
      return;
    }

    grouped[category].push({
      ...item,
      id: item?.id ?? item?.chem_id ?? item?.chemical,
      value: item?.chemical,
      label: item?.chemical,
    });
  });

  return grouped;
};

export const getSelectedChemicalFromControls = (formControls = {}) => {
  const chemicalControl = formControls?.[CHEMICAL_FIELDS.chemical];
  const selectedValue = chemicalControl?.value;
  const options = Array.isArray(chemicalControl?.options)
    ? chemicalControl.options
    : [];

  if (!selectedValue) {
    return options[0] || null;
  }

  return (
    options.find(
      (option) =>
        option?.value === selectedValue || option?.chemical === selectedValue,
    ) || null
  );
};

export const applySelectedChemicalFormState = (
  formControls,
  selectedChemical,
  categoryOptions,
) => {
  if (!formControls || typeof formControls !== "object") {
    return;
  }

  const nextCategoryOptions = Array.isArray(categoryOptions)
    ? categoryOptions
    : formControls?.[CHEMICAL_FIELDS.chemical]?.options || [];

  formControls[CHEMICAL_FIELDS.chemical] = {
    ...formControls[CHEMICAL_FIELDS.chemical],
    options: nextCategoryOptions,
    disabled: nextCategoryOptions.length === 0,
    value: selectedChemical?.chemical || selectedChemical?.value || "",
  };

  formControls[CHEMICAL_FIELDS.bulkConcentration] = {
    ...formControls[CHEMICAL_FIELDS.bulkConcentration],
    value: selectedChemical?.bulk_concentration ?? "0",
    range: getChemicalConcentrationRange(selectedChemical?.chemical),
    disabled: !selectedChemical,
  };

  formControls[CHEMICAL_FIELDS.displayName] = {
    ...formControls[CHEMICAL_FIELDS.displayName],
    value: formatChemicalDisplayName(
      selectedChemical?.symbol,
      selectedChemical?.bulk_concentration,
    ),
  };

  Object.entries(SELECTED_CHEMICAL_FIELD_CONFIG).forEach(
    ([fieldKey, config]) => {
      const fieldValue =
        selectedChemical?.[config.sourceKey] ?? config.fallback ?? "";

      formControls[fieldKey] = {
        ...formControls[fieldKey],
        value: fieldValue,
        disabled: selectedChemical
          ? Boolean(config.disabled)
          : Boolean(config.disabledWhenNoSelection ?? true),
      };
    },
  );
};

const cloneControlConfig = (config = {}) => {
  return Object.entries(config).reduce((acc, [key, value]) => {
    if (Array.isArray(value)) {
      acc[key] = value.map((item) => ({ ...item }));
      return acc;
    }

    if (value && typeof value === "object") {
      acc[key] = { ...value };
      return acc;
    }

    acc[key] = value;
    return acc;
  }, {});
};

const getEditableFields = (isAddMode, isCustomChemicalEditable = false) => {
  if (isAddMode) {
    return [
      CHEMICAL_FIELDS.chemical,
      CHEMICAL_FIELDS.symbol,
      CHEMICAL_FIELDS.bulkConcentration,
      CHEMICAL_FIELDS.bulkDensity,
      CHEMICAL_FIELDS.price,
      CHEMICAL_FIELDS.unit,
    ];
  }

  if (isCustomChemicalEditable) {
    return [
      CHEMICAL_FIELDS.bulkConcentration,
      CHEMICAL_FIELDS.price,
      CHEMICAL_FIELDS.unit,
    ];
  }

  return [CHEMICAL_FIELDS.price, CHEMICAL_FIELDS.unit];
};

/**
 * Formats the chemical form data by populating the form controls with the provided chemical data.
 * It also sets the options for category and chemical fields based on the provided categories and chemicals.
 *
 * @param {Object} chemicalData - The data of the chemical to be formatted.
 * @param {Array} options.chemicalCategories - The list of chemical categories.
 * @param {Object} options.chemicalsByCategoryId - The chemicals grouped by category ID.
 * @returns {Object} - The formatted form controls with populated values and options.
 */
export const formatChemicalDataToFormData = (
  t,
  chemicalData,
  isAddMode,
  isCustomChemicalEditable = false,
) => {
  // Clone while preserving function properties (e.g. validate) that structuredClone cannot handle.
  let formControls = cloneControlConfig(ADD_EDIT_FORM_CONTROLS(t));
  const editableFields = getEditableFields(isAddMode, isCustomChemicalEditable);

  if (chemicalData) {
    formControls = Object.entries(formControls).reduce(
      (updatedControls, [fieldKey, fieldConfig]) => {
        const updatedFieldConfig = {
          ...fieldConfig,
          value: chemicalData[fieldKey],
          disabled: !editableFields.includes(fieldKey),
        };

        if (fieldKey === CHEMICAL_FIELDS.bulkConcentration) {
          updatedFieldConfig.range = getChemicalConcentrationRange(
            chemicalData?.chemical,
          );
        }

        if (fieldKey === CHEMICAL_FIELDS.category && chemicalData?.category) {
          updatedFieldConfig.options = [
            {
              id: chemicalData.category,
              value: chemicalData.category,
            },
          ];
        }

        return { ...updatedControls, [fieldKey]: updatedFieldConfig };
      },
      {},
    );
  } else if (isAddMode) {
    const categoryOptions = ADD_CHEMICAL_CATEGORY_OPTIONS;

    formControls = Object.entries(formControls).reduce(
      (updatedControls, [fieldKey, fieldConfig]) => {
        const updatedFieldConfig = {
          ...fieldConfig,
          disabled: true,
        };

        if (fieldKey === CHEMICAL_FIELDS.category) {
          updatedFieldConfig.type = "dropdown";
          updatedFieldConfig.value = "";
          updatedFieldConfig.options = categoryOptions;
          updatedFieldConfig.labelKey = "value";
          updatedFieldConfig.disabled = false;
        }

        if (fieldKey === CHEMICAL_FIELDS.chemical) {
          updatedFieldConfig.type = "dropdown";
          updatedFieldConfig.options = [];
          updatedFieldConfig.labelKey = "value";
          updatedFieldConfig.value = "";
          updatedFieldConfig.disabled = true;
        }

        if (fieldKey === CHEMICAL_FIELDS.bulkConcentration) {
          updatedFieldConfig.range = getChemicalConcentrationRange("");
        }

        if (fieldKey === CHEMICAL_FIELDS.bulkDensity) {
          updatedFieldConfig.value = "0.00";
          updatedFieldConfig.disabled = true;
        }

        if (fieldKey === CHEMICAL_FIELDS.unit) {
          updatedFieldConfig.value = "";
          updatedFieldConfig.disabled = true;
        }

        if (editableFields.includes(fieldKey)) {
          updatedFieldConfig.disabled = true;
        }

        return { ...updatedControls, [fieldKey]: updatedFieldConfig };
      },
      {},
    );
  }

  return formControls;
};
